import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  grid, GridProps as BaseGridProps, space, SpaceProps
} from 'styled-system'
import GridItem from './GridItem'

export interface GridProps extends BaseGridProps, React.HTMLAttributes<HTMLDivElement>, SpaceProps {
  /**
  The number of columns the Grid should contain
  */
  columns?: number
  /**
  Removes the outer padding of the Grid when set to `true`
  */
  noPadding?: boolean
  /**
  Enables the shortcut to view the Grid columns
  */
  controlled?: boolean
}

interface StyledGridProps extends BaseGridProps, React.HTMLAttributes<HTMLDivElement> {
  $columns?: number
}

const StyledGrid = styled.div<StyledGridProps>`
  ${grid}
  ${space}
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columns ?? 12}, minmax(0, 1fr));
  position: relative;
  box-sizing: border-box;
`

const OverlayGrid = styled(StyledGrid)`
  position: absolute;
  grid-auto-columns: 12fr;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  pointer-events: none;
`

const OverlayBox = styled(GridItem)`
  background: #00000030;
  pointer-events: none;
`

/**
 * The Grid component is based around a 12 columnd Grid and uses [CSS Grid](https://css-tricks.com/snippets/css/complete-guide-grid/) to generate it. <br/>
 * Standard CSS Grid properties can be used, although a more refined
 * system as been set up for ease of use. This emphasises using `span`.<br/>
 * By default, the grid has outter padding equal to the column and row gap,
 * although this can be turned off by using the `noPadding` prop.<br/>
 * The grid is responsive and changes the gap and padding based on screen width.<br/>
 * <br/>
 * To position the `GridItem`s, use the props `grid-column` and `grid-row`.
 * Both use the same format of `start` / `end`. <br/>
 * &nbsp;&nbsp;e.g. 2 / 5 would mean the item starts in position 2 and ends in position 5.
 * If you want the item to fit into the next possible position, use `auto` as the start.<br />
 * The `span` keyword can also be utilised as part of the end value to specify
 * how many columns/rows you would like the item to stretch over.<br/>
 * <br/>
 * Both props are fully responsive and accept an array of values that correspond to the
 * breakpoints provided within the theme object. See the examples below on how to do this.
 */
const Grid = ({
  columns = 12, noPadding, children, controlled = false, ...props
}: GridProps) => {
  const [showOverlay, toggleOverlay] = useState(false)

  const escFunction = useCallback((event: { keyCode: number }) => {
    if (event.keyCode === 27) {
      toggleOverlay((prev) => !prev)
    }
  }, [])

  useEffect(() => {
    if (controlled && process.env.NODE_ENV !== 'production') {
      document.addEventListener('keydown', escFunction, false)
    }

    return () => {
      document.removeEventListener('keydown', escFunction, false)
    }
  })

  const createOverlayBoxes = () => {
    const boxes = []
    for (let i = 0; i < columns; i += 1) {
      boxes.push(<OverlayBox key={i} />)
    }
    return boxes
  }

  return (
    <StyledGrid gridColumnGap={[3, 5]} px={noPadding ? 0 : [3, 5]} $columns={columns} {...props}>
      {showOverlay && (
        <OverlayGrid
          gridColumnGap={[3, 5]}
          px={noPadding ? 0 : [3, 5]}
          $columns={columns}
          {...props}
        >
          {createOverlayBoxes()}
        </OverlayGrid>
      )}
      {children}
    </StyledGrid>
  )
}

export default Grid
